import { DirectionalHint, getTheme, Icon, Stack, StackItem, Text, TooltipHost, TooltipOverflowMode } from '@fluentui/react';
import { IC } from 'components/SVGIcon';
import IvicosStrings from 'kits/language/stringKit';
import { getUrlLocation } from 'kits/urlKit';
import React from 'react';
import { useNavigate, useParams } from 'react-router';

interface ILowerHeaderDropdownProps {
    dropdownType: 'joinedOrgs' | 'l0AreaGroup' | 'l1AreaGroup' | 'areaDropdown';
    handleDropdownToggle: (dropdown: string) => void;
    textColor: string;
    headerLabel: {
        icon: React.ReactNode;
        title: string;
        titleId?: string;
    };
    isDropdownOpen: boolean;
    isAnimating: boolean;
    items: any[];
    itemIcon: React.ReactNode;
    dropdownLeftShift?: number;
    dropdownInitialItem?: {
        icon: React.ReactNode;
        title: string;
    };
    redirectToLobby?: {
        icon: React.ReactNode;
        title: string;
    };
    isSelected?: boolean;
    handleRedirectionFromDropdown?: (item?: any, isLobby?: boolean, type?: 'area' | 'areaGroup' | 'lobby') => void;
}

const theme = getTheme();

const LowerHeaderDropdown: React.FC<ILowerHeaderDropdownProps> = (props) => {
    const {
        dropdownType,
        handleDropdownToggle,
        textColor,
        headerLabel,
        isDropdownOpen,
        isAnimating,
        items,
        dropdownInitialItem,
        isSelected,
        itemIcon,
        dropdownLeftShift,
        redirectToLobby,
        handleRedirectionFromDropdown
    } = props;
    const routerHistory = useNavigate();
    const { areaId: areaIdFromUrl } = useParams<{ areaId?: string }>();
    const { isInDashboardLobby, isInDashboardLevel0, isInDashboardLevel1, isInsideArea } = getUrlLocation();

    const type =
        dropdownType === 'areaDropdown' ? 'area' : dropdownType === 'l0AreaGroup' ? 'areaGroup' : dropdownType === 'l1AreaGroup' ? 'areaGroup' : 'lobby';

    const isNotInLobby = headerLabel.title === 'LOBBY' && !isInDashboardLobby;
    const isNotInsideLevel1 = headerLabel.title === IvicosStrings.areaGroup && !isInDashboardLevel1;
    const isNotInsideArea = headerLabel.title === IvicosStrings.enterOffice && !isInsideArea;

    const handleTitleClick = (title: string, titleId?: string) => {
        const isAtLobby = title === 'LOBBY' && isInDashboardLobby && dropdownType === 'l0AreaGroup';
        const isAtLevel0 = titleId && titleId === localStorage.getItem('l0AGId') && isInDashboardLevel0 && dropdownType === 'l0AreaGroup';
        const isAtLevel1 = titleId && titleId === localStorage.getItem('l1AGId') && isInDashboardLevel1 && dropdownType === 'l1AreaGroup';
        const areaLevel = JSON.parse(localStorage.getItem('areaLevel') || '{}');
        const isAtSameArea = isInsideArea && areaIdFromUrl === areaLevel.areaId && dropdownType === 'areaDropdown';

        if (isNotInLobby) {
            routerHistory('/dashboard');
            return;
        }

        if (isAtLobby || isAtLevel0 || isAtLevel1 || isNotInsideArea || isAtSameArea || (isInDashboardLevel0 && title === IvicosStrings.areaGroup)) {
            handleDropdownToggle(dropdownType);
            return;
        } else {
            const level = dropdownType === 'l0AreaGroup' ? 'L0' : 'L1';
            routerHistory(`/group/${titleId}/${level}/subAreas`);
        }
    };

    return (
        <Stack horizontal verticalAlign="center" style={{ marginLeft: 30 }}>
            <Stack horizontal verticalAlign="center">
                <Stack
                    horizontal
                    verticalAlign="center"
                    onClick={() => handleTitleClick(headerLabel.title, headerLabel.titleId)}
                    styles={{ root: { cursor: 'pointer' } }}
                >
                    <IC variant={'custom'} size={24} iconColor={textColor}>
                        {headerLabel.icon}
                    </IC>
                    {/* Single line with CSS ellipsis; the tooltip only shows when the title is actually cut off */}
                    <Text
                        as="div"
                        block
                        nowrap
                        style={{
                            marginLeft: 16,
                            color: textColor,
                            fontWeight: isNotInsideLevel1 || isNotInsideArea ? 'normal' : 'bold',
                            width: 140
                        }}
                    >
                        <TooltipHost content={headerLabel.title} overflowMode={TooltipOverflowMode.Parent} directionalHint={DirectionalHint.bottomCenter}>
                            {headerLabel.title}
                        </TooltipHost>
                    </Text>
                </Stack>

                <Icon
                    iconName={'ChevronDownMed'}
                    styles={{
                        root: {
                            fontSize: '14px',
                            marginTop: 2,
                            marginLeft: 8,
                            color: textColor,
                            cursor: 'pointer',
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                    }}
                    style={{
                        transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                    onClick={() => handleDropdownToggle(dropdownType)}
                />
            </Stack>
            {(isDropdownOpen || isAnimating) && items.length > 0 && (
                <Stack
                    style={{
                        position: 'absolute',
                        top: 95,
                        left: dropdownLeftShift,
                        width: 200,
                        zIndex: 100,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        backgroundColor: theme.palette.neutralLighter,
                        color: '#000',
                        overflow: 'hidden',
                        transform: isDropdownOpen ? 'scaleY(1)' : 'scaleY(0)',
                        transformOrigin: 'top',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: isDropdownOpen ? 1 : 0.8,
                        transitionProperty: 'transform, opacity'
                    }}
                >
                    <Stack
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: 8 }}
                        style={{ backgroundColor: theme.palette.blackTranslucent40, width: '100%', height: '58px', padding: 16 }}
                    >
                        <IC variant={'custom'} size={24} iconColor={'#fff'}>
                            {dropdownInitialItem?.icon}
                        </IC>
                        <Text style={{ color: '#fff' }}>{dropdownInitialItem?.title}</Text>
                    </Stack>
                    {redirectToLobby && !isInDashboardLobby && (
                        <Stack
                            horizontal
                            verticalAlign="center"
                            tokens={{ childrenGap: 8 }}
                            styles={{
                                root: {
                                    width: '100%',
                                    height: '58px',
                                    padding: 14,
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${theme.palette.blackTranslucent40}`,
                                    '&:hover': {
                                        backgroundColor: theme.palette.neutralLight
                                    }
                                }
                            }}
                            onClick={() => handleRedirectionFromDropdown && handleRedirectionFromDropdown(null, true, 'lobby')}
                        >
                            <IC variant={'custom'} size={20}>
                                {redirectToLobby.icon}
                            </IC>
                            <Text style={{ color: '#000' }}>LOBBY</Text>
                        </Stack>
                    )}
                    <Stack style={{ padding: 16, maxHeight: '300px', overflowY: 'auto' }}>
                        {items.map((item, index) => {
                            return (
                                <Stack
                                    key={item.id}
                                    horizontal
                                    verticalAlign="center"
                                    tokens={{
                                        childrenGap: 8
                                    }}
                                    styles={{
                                        root: {
                                            padding: '12px 4px',
                                            cursor: isSelected ? 'default' : 'pointer',
                                            transition: 'background-color 0.2s ease',
                                            transform: isDropdownOpen ? 'translateY(0)' : `translateY(-${(index + 1) * 10}px)`,
                                            opacity: isDropdownOpen ? 1 : 0,
                                            transitionDelay: isDropdownOpen ? `${index * 50}ms` : '0ms',
                                            transitionDuration: '0.3s',
                                            backgroundColor: isSelected ? theme.palette.themePrimary : 'transparent',
                                            color: isSelected ? theme.palette.white : theme.palette.neutralPrimary,
                                            fontWeight: isSelected ? '600' : 'normal',
                                            margin: '2px 0',
                                            '&:hover': {
                                                backgroundColor: isSelected ? theme.palette.themePrimary : theme.palette.neutralLight
                                            }
                                        }
                                    }}
                                    onClick={() => {
                                        handleRedirectionFromDropdown?.(item, false, type);
                                    }}
                                >
                                    <StackItem>
                                        <IC variant={isSelected ? 'light' : 'dark'} size={16}>
                                            {itemIcon}
                                        </IC>
                                    </StackItem>
                                    <Text
                                        as="div"
                                        block
                                        nowrap
                                        styles={{
                                            root: {
                                                color: isSelected ? theme.palette.white : theme.palette.neutralPrimary,

                                                fontWeight: isSelected ? '600' : 'normal'
                                            }
                                        }}
                                    >
                                        <TooltipHost
                                            content={item.name}
                                            overflowMode={TooltipOverflowMode.Parent}
                                            directionalHint={DirectionalHint.bottomCenter}
                                        >
                                            {item.name}
                                        </TooltipHost>
                                    </Text>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

export default LowerHeaderDropdown;
